/**
 * Data layer for `cluster-analysis-group-level-anomaly`. Thin typed fetch
 * wrappers over /api/v1/cluster-analysis-group-level-anomaly. Raw `fetch()`
 * is confined to this file per the dash contract (D6).
 *
 * Endpoints (BE shape):
 *   POST   /                         -> { ok, event_id, group_id, created_at }
 *   GET    /                         -> { items: ClusterAnomalyEvent[] }
 *   GET    /groups                   -> { groups: GroupRollup[] }
 */

export const CLUSTER_SIGNALS = [
	"ip_cluster",
	"device_farm",
	"emulator_farm",
	"geo_mismatch",
	"fingerprint_cluster",
	"asn_cluster",
] as const;

export type ClusterSignal = (typeof CLUSTER_SIGNALS)[number];

export interface ClusterAnomalyEvent {
	id: number;
	tenant_id: string;
	event_id: string;
	group_id: string;
	publisher_id: string;
	signal: string;
	score: number;
	payload_json: string;
	created_at: number;
}

export interface ListClusterAnomalyResponse {
	items: ClusterAnomalyEvent[];
}

export interface GroupRollup {
	group_id: string;
	observation_count: number;
	publisher_count: number;
	signal_count: number;
	avg_score: number;
	max_score: number;
	flagged: boolean;
}

export interface ListGroupRollupResponse {
	groups: GroupRollup[];
}

export interface CreateClusterAnomalyBody {
	event_id: string;
	group_id: string;
	publisher_id: string;
	signal: ClusterSignal | string;
	score: number;
}

export interface CreateClusterAnomalyResponse {
	ok: boolean;
	event_id: string;
	group_id: string;
	created_at: number;
}

const BASE = "/api/v1/cluster-analysis-group-level-anomaly";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListClusterAnomalyResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListClusterAnomalyResponse>(res);
}

export async function listGroups(): Promise<ListGroupRollupResponse> {
	const res = await fetch(`${BASE}/groups`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListGroupRollupResponse>(res);
}

export async function create(
	body: CreateClusterAnomalyBody,
): Promise<CreateClusterAnomalyResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateClusterAnomalyResponse>(res);
}

export const clusterAnalysisGroupLevelAnomalyQueryKeys = {
	all: ["cluster-analysis-group-level-anomaly"] as const,
	list: () => [...clusterAnalysisGroupLevelAnomalyQueryKeys.all, "events"] as const,
	groups: () => [...clusterAnalysisGroupLevelAnomalyQueryKeys.all, "groups"] as const,
	detail: (id: string) =>
		[...clusterAnalysisGroupLevelAnomalyQueryKeys.all, id] as const,
};
